import { PropsWithChildren } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  ReviewProcessTargetsManagedByFilterInput,
  useProcessTargetDataTable,
} from 'generated/resources/datatable.graphql'
import {
  PerformanceDtosReviewProcessTargetAgreementCompletionStatusEnum,
  PerformanceReviewEvaluationStatusEnum,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import ScoreTag from 'modules/performance_analytics/components/ScoreTag'
import { AgreementStatusTag } from 'modules/performance_process/components/agreements/StatusTag'
import { TargetAvatar } from 'modules/performance_process/components/common/TargetAvatar'
import { InlineEmployee } from 'modules/performance_v2/components/InlineEmployee'

import { Actions } from './helpers/Actions'
import { AlignmentScoreTag } from './helpers/AlignmentScoreTag'
import { EvaluationLink } from './helpers/EvaluationLink'
import { PeerEvaluationsStats } from './helpers/PeerEvaluationsStats'
import { TargetEvaluationsStatusTag } from './helpers/StatusTag'

type Props = {
  agreementEnabled: boolean
  showEmployeeScores: boolean
  reviewId: number
  managedByFilter: ReviewProcessTargetsManagedByFilterInput | undefined
  reviewerStrategies: PerformanceReviewerStrategiesStrategyEnum[]
  employeeIdsFilter?: number[]
  agreementCompletionStatusFilter?: PerformanceDtosReviewProcessTargetAgreementCompletionStatusEnum
  evaluationsCompletionStatusFilter?: PerformanceReviewEvaluationStatusEnum
  teamIdsFilter?: number[]
  tenureDatesFilter?: string[]
  workplaceIdsFilter?: number[]
  legalEntityIdsFilter?: number[]
  searchFilter?: string
  scoresFilter?: number[]
}

const ProcessTargetDataTable = ({
  reviewId,
  agreementEnabled,
  managedByFilter,
  employeeIdsFilter,
  agreementCompletionStatusFilter,
  evaluationsCompletionStatusFilter,
  teamIdsFilter,
  tenureDatesFilter,
  workplaceIdsFilter,
  legalEntityIdsFilter,
  searchFilter,
  scoresFilter,
  showEmployeeScores,
  reviewerStrategies = [],
}: PropsWithChildren<Props>) => {
  const selfReviewerStrategyEnabled = reviewerStrategies.includes('self')
  const managerReviewerStartegyEnabled = reviewerStrategies.includes('manager')
  const peersReviewerStartegyEnabled = reviewerStrategies.includes('peers')
  const { access: currentAccess } = useCurrentSession()

  const source = useProcessTargetDataTable(
    [
      {
        identifier: 'target',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.employee'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ access }}',
        colSpan: 2,
        render: ({ access }) => {
          return (
            access && (
              <Box maxWidth="s240">
                <TargetAvatar access={access} />
              </Box>
            )
          )
        },
      },
      {
        identifier: 'manager',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.manager'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ access_employee_manager_access_fullName }}',
        colSpan: 2,
        render: ({ access_employeeName_managerName }) => {
          return (
            access_employeeName_managerName && (
              <Box maxWidth="s240">
                <InlineEmployee hideAvatar employee={access_employeeName_managerName} />
              </Box>
            )
          )
        },
      },
      {
        identifier: 'final_score',
        title: i18n.t('performance_process.reviews.target_datatable.final_score.title'),
        fixed: true,
        hidden: !showEmployeeScores,
        kind: ProjectionKind.Custom,
        width: 152,
        template: '{{ reviewFinalEmployeeScore.score }}',
        render: ({ reviewFinalEmployeeScore }) => (
          <Box width="s120" data-testid="employee-final-score">
            <ScoreTag
              score={reviewFinalEmployeeScore?.score}
              maxScore={reviewFinalEmployeeScore?.scaleMax}
            />
          </Box>
        ),
      },
      {
        identifier: 'alignment',
        title: i18n.t('performance_process.reviews.target_datatable.alignment.title'),
        fixed: true,
        kind: ProjectionKind.Custom,
        colSpan: 2,
        template: '{{ insightScore.alignmentScore }}',
        render: ({ insightScore }) =>
          insightScore?.alignmentScore ? (
            <AlignmentScoreTag alignmentScore={insightScore.alignmentScore} />
          ) : (
            <Box alignItems="center">-</Box>
          ),
      },
      {
        identifier: 'manager_review',
        title: i18n.t('performance_process.reviews.target_datatable.manager_review.title'),
        fixed: true,
        hidden: !managerReviewerStartegyEnabled,
        kind: ProjectionKind.Custom,
        template: '{{ evaluations.managerReview }}',
        render: ({ evaluations, id, access: targetAccess }) => {
          const managerEvaluation = evaluations?.find(
            (evaluation) => evaluation?.evaluationType === 'manager'
          )
          return managerEvaluation ? (
            <Box width="s120">
              <EvaluationLink
                evaluation={managerEvaluation}
                reviewId={reviewId}
                targetId={id}
                isCheckingOwnResults={currentAccess?.id == targetAccess?.id}
              />
            </Box>
          ) : (
            <Box alignItems="center">-</Box>
          )
        },
      },
      {
        identifier: 'self_review',
        title: i18n.t('performance_process.reviews.target_datatable.self_review.title'),
        fixed: true,
        hidden: !selfReviewerStrategyEnabled,
        kind: ProjectionKind.Custom,
        template: '{{ evaluations.selfReview }}',
        render: ({ evaluations, id, access: targetAccess }) => {
          const selfEvaluation = evaluations?.find(
            (evaluation) => evaluation?.evaluationType === 'self'
          )
          return selfEvaluation ? (
            <Box width="s80">
              <EvaluationLink
                evaluation={selfEvaluation}
                reviewId={reviewId}
                targetId={id}
                isCheckingOwnResults={currentAccess?.id == targetAccess?.id}
              />
            </Box>
          ) : (
            <Box alignItems="center">-</Box>
          )
        },
      },
      {
        identifier: 'peer_review',
        title: i18n.t('performance_process.reviews.target_datatable.peer_review.title'),
        fixed: true,
        hidden: !peersReviewerStartegyEnabled,
        kind: ProjectionKind.Custom,
        width: 170,
        template: '{{ evaluations.peerReview }}',
        render: ({ evaluations }) => {
          const peerEvaluations = evaluations?.filter(
            (evaluation) => evaluation?.evaluationType === 'peers'
          )
          return peerEvaluations?.length > 0 ? (
            <PeerEvaluationsStats evaluations={peerEvaluations} />
          ) : (
            <Box alignItems="center">-</Box>
          )
        },
      },
      {
        identifier: 'results',
        title: i18n.t('performance_process.reviews.target_datatable.results.title'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ Results }}',
        colSpan: 2,
        render: ({ evaluations }) => {
          const totalEvaluations = evaluations?.length
          const publishedEvaluations = evaluations?.filter(
            (evaluation) => evaluation?.status === 'published'
          )
          return (
            totalEvaluations != null &&
            publishedEvaluations != null && (
              <Box width="s120">
                <TargetEvaluationsStatusTag
                  evaluationsConnection_totalCount={totalEvaluations}
                  publishedEvaluationsConnection_totalCount={publishedEvaluations.length}
                />
              </Box>
            )
          )
        },
      },
      {
        identifier: 'agreement',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.agreement'),
        fixed: true,
        hidden: !agreementEnabled,
        kind: ProjectionKind.Custom,
        template: '{{ Agreement }}',
        colSpan: 2,
        render: ({ agreement }) => <AgreementStatusTag agreement={agreement ?? null} />,
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 100,
        fixed: true,
        sticky: true,
        render: ({ access }) => {
          return access?.id && <Actions reviewId={Number(reviewId)} targetId={Number(access.id)} />
        },
      },
    ],
    {
      variables: {
        managedByFilter: managedByFilter,
        performanceReviewProcessIds: [Number(reviewId)],
        searchValue: searchFilter,
        employeeIds: employeeIdsFilter,
        agreementCompletionStatus: agreementCompletionStatusFilter,
        evaluationsCompletionStatus: evaluationsCompletionStatusFilter,
        finalScores: scoresFilter,
        teamIds: teamIdsFilter,
        tenureDates: tenureDatesFilter,
        workplaceIds: workplaceIdsFilter,
        legalEntityIds: legalEntityIdsFilter,
      },
    }
  )

  return (
    <DataTable.Provider
      persistFilters={false}
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
      }}
    >
      <DataTable.Table autoWidth />
    </DataTable.Provider>
  )
}

export default ProcessTargetDataTable
